import { computed } from "mobx";
import { isDbNetworkEventWithSearchResults } from "../../ServiceWorker/NetworkEventDbSearchWebWorker";
import { AllOption } from "./SingleOriginFilterState";
import { getRegisteredDomain } from "../../lib/utils/domains";
import { RecordingSearchState } from "./RecordingSearchState";
import { InstrumentationEventDomEventFired, isDomEventFiredEvent } from "../../lib/Events/InstrumentationEvent";
import { DbRecordedEvent, UUID } from "../../State/EvidenceDB/DatabaseTypes";
import { mapGetOrCreate } from "../../lib/utils/mapGetOrCreate";
import type { SearchResultEvent } from "../../lib/DbEventSearcher";
import type { DbNetworkEventWithSearchResults } from "../../lib/searchRecordedEvent";


export class FilteredSearchResultsState {
	constructor(
		protected readonly recordingSearchState: RecordingSearchState,
	) {}


	@computed get recordingUuid() { return this.recordingSearchState.recordingUuid; };


	searchTermFilter = (searchTerm: string) => this.recordingSearchState.showOnlySearchTerm == null ? true :
		searchTerm === this.recordingSearchState.showOnlySearchTerm

	@computed get searchTerms(): string[] {
		return this.recordingSearchState.searchTerms;
	}

	@computed get filteredSearchResults(): SearchResultEvent[] {
		const domFiredEventsForElementUuid = new Map<UUID, DbRecordedEvent<InstrumentationEventDomEventFired>[]>
		return this.recordingSearchState.searchState.results
			// filter events by sourcePage (tab) url and destination url.
			.filter( s => {
				// make sure the source page matches the source url filter
				if (!this.recordingSearchState.sourcePageUrlFilterState.urlFilter( s.tab.url )) {
					return false;
				}
				// if this is a network event if will have a destination url, and
				// make sure it matches the destination url filter
				if (!isDbNetworkEventWithSearchResults(s) || this.recordingSearchState.destinationUrlFilterState.isFilterAll)
					// No need to filter by destination
					return true;
				if (this.recordingSearchState.destinationUrlFilterState.isFilterThirdParties) {
					// Third party filtering is done here since we have knowledge of source and destination
					return getRegisteredDomain(s.destination.url) !== getRegisteredDomain(s.tab.url);
				}
				// Filtering of specific domains is done by the filter
				return this.recordingSearchState.destinationUrlFilterState.urlFilter(s.destination.url)
			})
			// Filter search results within each event by search term
			.map((event) => {
				if (isDbNetworkEventWithSearchResults(event)) {
					// remove search results for the network event that don't match the search term filter
						return { ...event,
						searchResults: event.searchResults.filter((searchResult) => {
							// Filter for selected search term
							if (this.recordingSearchState.showOnlySearchTerm != null &&
								searchResult.originalSearchTerm !== this.recordingSearchState.showOnlySearchTerm
							) {
								return false;
							}
							// Filter for selected direction
							if (this.recordingSearchState.dataFlowDirection != AllOption &&
								searchResult.direction !== this.recordingSearchState.dataFlowDirection
							) {
								return false;
							}
							return true;
						})
					} satisfies DbNetworkEventWithSearchResults;
				} else if (isDomEventFiredEvent(event) && (event.raw.domOrCustomEventName === "input" || event.raw.domOrCustomEventName === "change")) {					
					// Track input/change events so that we can only show the final input/change
					mapGetOrCreate(domFiredEventsForElementUuid, event.raw.elementUUID, () => []).push(event);
				}
				return event;
			})
			// Remove events where all the search results were filtered out
			.filter((event) => {
				if ( isDbNetworkEventWithSearchResults(event)) {
					return event.searchResults.length > 0;
				} else if (isDomEventFiredEvent(event) && (event.raw.domOrCustomEventName === "input" || event.raw.domOrCustomEventName === "change")) {
					// remove input/change events that are prefixes of future events within the next 3 seconds
					if ((domFiredEventsForElementUuid.get(event.raw.elementUUID) ?? []).find( e =>
						e.whenRecordedIso > event.whenRecordedIso &&
						new Date(e.whenRecordedIso).getTime() < (new Date(event.whenRecordedIso).getTime() + 3000) &&
						(e.raw.value ?? "").startsWith(event.raw.value ?? "")
					)) {
						return false;
					}
				}
				return true;

			});
	}

}
